import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { AssistantAction, AssistantConversation, AssistantLink, AssistantState, AssistantStep } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { t, tMessage } from '../lib/i18n';
import { Icon } from './Icon';
import { CatBed } from './CatBed';
import { assistantContext } from '../lib/assistantContext';
import { assistantLocked, noteSending, refreshBusy, useAssistantLocked, useScreenWorking } from '../lib/screen';
import { MascotMark } from './Mascot';
import { Dialog } from './Overlay';
import { toast } from './Toasts';

/**
 * 馍馍 the assistant: the cat in the corner opens a chat. It runs on the server with the AI picked for "assistant" and works the app
 * through its tools; replies can carry action cards (sending mail, deleting events, changing the tagging rules, unsubscribing — they wait
 * for the user's OK) and buttons that open a message, a calendar day or a settings page.
 */

/** What the assistant is doing, by tool. */
export const STEP: Record<string, () => string> = {
  search_mail: () => t('在搜邮件…'),
  list_mail: () => t('在看邮件列表…'),
  read_mail: () => t('在读邮件…'),
  read_thread: () => t('在读整个对话…'),
  read_attachment: () => t('在看附件…'),
  create_file: () => t('在做文件…'),
  act_on_mail: () => t('在整理邮件…'),
  create_draft: () => t('在写草稿…'),
  send_email: () => t('在准备发送…'),
  list_events: () => t('在看日程…'),
  free_time: () => t('在找空闲时间…'),
  create_event: () => t('在加日程…'),
  update_event: () => t('在改日程…'),
  delegate: () => t('请小助手帮忙…'),
  memory_write: () => t('在记笔记…'),
  ui_look: () => t('在看屏幕…'),
  ui_act: () => t('在操作屏幕…'),
  ui_navigate: () => t('在打开页面…'),
  ui_peek: () => t('在看页面…'),
};

/** A step it took, in the list under its reply. */
const STEP_NAME: Record<string, () => string> = {
  search_mail: () => t('搜邮件'),
  list_mail: () => t('看邮件列表'),
  read_mail: () => t('读邮件'),
  read_thread: () => t('读整个对话'),
  read_attachment: () => t('看附件'),
  create_file: () => t('做文件'),
  act_on_mail: () => t('整理邮件'),
  create_draft: () => t('写草稿'),
  send_email: () => t('准备发送'),
  list_events: () => t('看日程'),
  free_time: () => t('找空闲时间'),
  create_event: () => t('加日程'),
  update_event: () => t('改日程'),
  delegate: () => t('请小助手帮忙'),
  memory_write: () => t('记笔记'),
  search_contacts: () => t('找联系人'),
  list_folders: () => t('看文件夹'),
  show_in_app: () => t('准备打开'),
  get_settings: () => t('看设置'),
  update_settings: () => t('改设置'),
  tagging_read: () => t('看分类规则'),
  tagging_rules_write: () => t('改分类规则'),
  ui_look: () => t('看屏幕'),
  ui_act: () => t('操作屏幕'),
  ui_navigate: () => t('打开页面'),
  ui_peek: () => t('看页面'),
};

const seconds = (ms: number) => (ms < 10_000 ? (ms / 1000).toFixed(1) : String(Math.round(ms / 1000)));

/** Steps one per line: done (how long), failed, or still going. */
function StepList({ steps }: { steps: AssistantStep[] }) {
  return (
    <ul className="steps">
      {steps.map((s, i) => (
        <li key={i} className={s.error ? 'steps__failed' : ''}>
          {s.ms === undefined ? <span className="spinner spinner--tiny" /> : <Icon name={s.error ? 'x' : 'check'} size={12} />}
          <span>{[STEP_NAME[s.tool]?.() ?? t('用了一个工具'), s.detail ? `「${s.detail}」` : '', s.ms !== undefined ? `${seconds(s.ms)}s` : ''].filter(Boolean).join(' ')}</span>
        </li>
      ))}
    </ul>
  );
}

/** Under a reply: how many steps it took and how long, click to see them. */
function StepsFooter({ steps, ms }: { steps: AssistantStep[]; ms?: number }) {
  const [open, setOpen] = useState(false);
  const label = [steps.length ? t('做了 {n} 步', { n: steps.length }) : '', ms !== undefined ? t('{s} 秒', { s: seconds(ms) }) : ''].filter(Boolean).join(' · ');
  return (
    <div className="steps-footer">
      <button type="button" className="steps-footer__toggle" disabled={!steps.length} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {label}
        {steps.length > 0 && <Icon name={open ? 'down' : 'right'} size={12} />}
      </button>
      {open && <StepList steps={steps} />}
    </div>
  );
}

/** The reply being worked on: what it has said so far (or what it is doing), and the steps it has taken. */
function Working({ progress }: { progress: { step: string | null; steps: AssistantStep[]; draft: string } | null }) {
  const last = progress?.steps.at(-1);
  const doing = last && last.ms === undefined ? last.tool : progress?.step;
  return (
    <div className="bubble bubble--assistant bubble--working">
      <div className="bubble--busy">
        <span className="spinner" />
        <span className={progress?.draft.trim() ? 'bubble__text' : ''}>{progress?.draft.trim() || (doing && STEP[doing]?.()) || t('馍馍在想…')}</span>
      </div>
      {progress && progress.steps.length > 0 && <StepList steps={progress.steps} />}
    </div>
  );
}

export interface AssistantDockProps {
  /** Open the place one of its buttons points to (App.tsx). */
  onFollow: (link: AssistantLink) => void;
}

/** Window event that opens the chat (the desktop cat's menu; the cat in the window, clicked: detail says where it is, the chat opens by it). */
export const ASSISTANT_OPEN = 'momomail:assistant-open';
/** Window event: the chat opened or closed (detail: open) — the cat in the window stays put while it is open. */
export const ASSISTANT_STATE = 'momomail:assistant-state';

/** The cat on the page (px): its middle, and its top and bottom (paws). */
export interface CatAnchor {
  x: number;
  top: number;
  bottom: number;
}

/** Where the chat goes by the cat: above it if it fits, else below, else on the side with more room (shorter); kept in the window. */
function besideCat(a: CatAnchor): CSSProperties {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const gap = 10;
  const width = Math.min(400, W - 32);
  let height = Math.min(600, H - 120);
  const above = a.top - gap - 16;
  const below = H - a.bottom - gap - 16;
  let top: number;
  if (above >= height) top = a.top - gap - height;
  else if (below >= height) top = a.bottom + gap;
  else if (above >= below) (height = Math.max(280, above)), (top = Math.max(16, a.top - gap - height));
  else (height = Math.max(280, below)), (top = Math.min(H - 16 - height, a.bottom + gap));
  const left = Math.min(W - 16 - width, Math.max(16, a.x - width / 2));
  return { left, top, width, height, right: 'auto', bottom: 'auto' };
}

export function AssistantDock({ catHome, canPickUp, ...props }: AssistantDockProps & { catHome: boolean; canPickUp: boolean }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<CatAnchor | null>(null);
  // The chat is for one instruction at a time: once it is sent, the chat steps out of the way (still there, hidden: what it is
  // sending carries on) while 馍馍 thinks and works — the pill at the top says what it is on, with stop — and comes back with the
  // reply when the turn is done. Meanwhile the cat and whatever opens the chat do nothing
  const locked = useAssistantLocked();
  const shown = open && !locked;
  useEffect(() => {
    const show = (e: Event) => {
      if (assistantLocked()) return;
      setAnchor((e as CustomEvent<CatAnchor | undefined>).detail ?? null);
      setOpen(true);
    };
    window.addEventListener(ASSISTANT_OPEN, show);
    return () => window.removeEventListener(ASSISTANT_OPEN, show);
  }, []);
  useEffect(() => void window.dispatchEvent(new CustomEvent(ASSISTANT_STATE, { detail: shown })), [shown]);
  return (
    <>
      <CatBed home={catHome} canPickUp={canPickUp} open={shown} onClick={() => !locked && (setAnchor(null), setOpen((v) => !v))} />
      {open && <AssistantPanel {...props} anchor={anchor} hidden={locked} onClose={() => setOpen(false)} />}
    </>
  );
}

function AssistantPanel({ onFollow, onClose, anchor, hidden }: AssistantDockProps & { onClose: () => void; anchor: CatAnchor | null; hidden: boolean }) {
  const [state, setState] = useState<AssistantState | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  /** How the turn is going (the assistant events; the state when opened while it works). */
  const [progress, setProgress] = useState<{ step: string | null; steps: AssistantStep[]; draft: string } | null>(null);
  const [stopping, setStopping] = useState(false);
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const s = await api().assistant.state();
      setState(s);
      if (s.busy) setProgress((p) => p ?? { step: s.step, steps: s.steps ?? [], draft: s.draft ?? '' });
    } catch (e) {
      toast(errorText(e), 'error');
    }
  }, []);

  useEffect(() => {
    void load();
    return api().onEvent((e) => {
      if (e.type !== 'assistant') return;
      setProgress(e.busy ? { step: e.step, steps: e.steps ?? [], draft: e.draft ?? '' } : null);
      if (!e.busy) void load();
    });
  }, [load]);

  // Follow along at the end (unless scrolled up to read something)
  useEffect(() => {
    const list = listRef.current;
    if (list && list.scrollHeight - list.scrollTop - list.clientHeight < 120) list.scrollTo({ top: list.scrollHeight, behavior: 'smooth' });
  }, [progress]);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [state?.messages.length, sending]);

  const stop = async () => {
    setStopping(true);
    try {
      await api().assistant.stop();
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setStopping(false);
    }
  };

  const send = async () => {
    const message = text.trim();
    // (busy, or working the screen — even should the chat still be showing: nothing goes until it is done; the text stays)
    if (!message || sending || state?.busy || assistantLocked()) return;
    setSending(true);
    noteSending();
    setText('');
    // Show the message right away; the reply comes when the turn is done
    setState((s) => (s ? { ...s, messages: [...s.messages, { id: 'pending', role: 'user', text: message, at: Date.now(), actions: [], links: [], error: false }] } : s));
    try {
      const before = new Set((state?.messages ?? []).map((m) => m.id));
      const next = await api().assistant.send(message, Intl.DateTimeFormat().resolvedOptions().timeZone, assistantContext());
      setState(next);
      // It was asked to take the user somewhere (or wrote a draft): go there
      const go = next.messages.filter((m) => !before.has(m.id)).flatMap((m) => m.links).find((l) => l.open || l.kind === 'draft');
      if (go) onFollow(go);
    } catch (e) {
      toast(errorText(e), 'error');
      setText(message);
      void load();
    } finally {
      setSending(false);
      setProgress(null);
      // (the turn's end unlocks the cat: as the server has it, should its event have been missed)
      refreshBusy();
    }
  };

  const answer = async (action: AssistantAction, approve: boolean) => {
    try {
      setState(await api().assistant.act(action.id, approve));
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const openChat = async (id: string) => {
    try {
      setState(await api().assistant.openConversation(id));
      setHistoryOpen(false);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const deleteChat = async (id: string) => {
    try {
      setState(await api().assistant.deleteConversation(id));
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const clear = async () => {
    setHistoryOpen(false);
    try {
      setState(await api().assistant.clear());
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const follow = onFollow;

  const busy = sending || !!state?.busy;
  const working = useScreenWorking();
  const actions = new Map((state?.actions ?? []).map((a) => [a.id, a]));

  return (
    <aside className={`assistant ${hidden ? 'is-tucked' : ''}`} style={anchor ? besideCat(anchor) : undefined} aria-label={t('馍馍助理')}>
      <header className="assistant__head">
        <MascotMark />
        <strong>{t('馍馍')}</strong>
        <span className="assistant__spacer" />
        <button className="btn btn--icon btn--quiet" title={t('馍馍的记忆')} aria-label={t('馍馍的记忆')} onClick={() => setMemoryOpen(true)}>
          <Icon name="file" size={15} />
        </button>
        <button className={`btn btn--icon btn--quiet ${historyOpen ? 'is-on' : ''}`} title={t('以前的对话')} aria-label={t('以前的对话')} aria-pressed={historyOpen} onClick={() => setHistoryOpen((v) => !v)}>
          <Icon name="clock" size={15} />
        </button>
        <button className="btn btn--icon btn--quiet" title={t('新对话')} aria-label={t('新对话')} disabled={busy || !state?.messages.length} onClick={() => void clear()}>
          <Icon name="plus" size={15} />
        </button>
        <button className="btn btn--icon btn--quiet" title={t('关闭')} aria-label={t('关闭')} onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </header>

      {historyOpen && state && <ChatHistory conversations={state.conversations ?? []} disabled={busy} onOpen={(id) => void openChat(id)} onDelete={(id) => void deleteChat(id)} />}
      <div className="assistant__list" ref={listRef} hidden={historyOpen}>
        {!state && <span className="spinner" />}
        {state && !state.ready && (
          <div className="notice notice--warn">
            {t('馍馍还没有可用的 AI。先在 设置 → AI 登录一个，再在"馍馍助理"里选它。')}{' '}
            <button className="field__link" onClick={() => onFollow({ kind: 'settings', tab: 'ai', label: '' })}>
              {t('去设置')}
            </button>
          </div>
        )}
        {state?.ready && !state.messages.length && (
          <p className="muted assistant__hello">{t('喵～我是馍馍。让我帮你找邮件、写回复、整理收件箱、安排日程，或者改设置都行。比如："把这周没回的重要邮件列出来"、"帮我约王五下周二开会"。')}</p>
        )}
        {state?.messages.map((m) => (
          <div key={m.id} className={`bubble bubble--${m.role} ${m.error ? 'bubble--error' : ''}`}>
            <div className="bubble__text">{m.role === 'assistant' && m.error ? tMessage(m.text) : m.text}</div>
            {m.role === 'assistant' && (m.steps?.length || m.ms !== undefined) ? <StepsFooter steps={m.steps ?? []} {...(m.ms !== undefined ? { ms: m.ms } : {})} /> : null}
            {m.links.length > 0 && (
              <div className="bubble__links">
                {m.links.map((l, i) => (
                  <button key={i} className="btn btn--small" onClick={() => follow(l)}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
            {m.actions.map((id) => {
              const a = actions.get(id);
              return a ? <ActionCard key={id} action={a} onAnswer={(ok) => void answer(a, ok)} /> : null;
            })}
          </div>
        ))}
        {busy && <Working progress={progress} />}
      </div>

      <form
        className="assistant__input"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <textarea
          value={text}
          rows={2}
          placeholder={working ? t('馍馍正在操作，做完再聊') : t('跟馍馍说点什么…')}
          disabled={!state?.ready || working}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send();
            }
          }}
        />
        {/* While it works: stop it instead (its reply keeps what it had said) */}
        {busy ? (
          <button type="button" className="btn btn--small" disabled={stopping} onClick={() => void stop()}>
            {stopping ? <span className="spinner" /> : t('停止')}
          </button>
        ) : (
          <button type="submit" className="btn btn--primary btn--small" disabled={!text.trim() || !state?.ready}>
            {t('发送')}
          </button>
        )}
      </form>
      {memoryOpen && <MemoryDialog onClose={() => setMemoryOpen(false)} />}
    </aside>
  );
}

function ActionCard({ action, onAnswer }: { action: AssistantAction; onAnswer: (approve: boolean) => void }) {
  const [busy, setBusy] = useState(false);
  const done = { done: t('已完成'), cancelled: t('已取消'), failed: t('没有成功'), pending: '' }[action.status];
  return (
    <div className={`action-card action-card--${action.status}`}>
      <div className="action-card__title">{action.title}</div>
      <pre className="action-card__detail">{action.detail}</pre>
      {action.status === 'pending' ? (
        <div className="action-card__buttons">
          <button className="btn btn--small" disabled={busy} onClick={() => onAnswer(false)}>
            {t('不用了')}
          </button>
          <button
            className="btn btn--small btn--primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              onAnswer(true);
            }}
          >
            {busy ? <span className="spinner" /> : t('确认')}
          </button>
        </div>
      ) : (
        <div className="action-card__result muted">
          {done}
          {action.result ? `：${tMessage(action.result)}` : ''}
        </div>
      )}
    </div>
  );
}

/** What 馍馍 remembers about the user (read-only: it keeps the notes itself; to forget something, tell it). */
function MemoryDialog({ onClose }: { onClose: () => void }) {
  const [memory, setMemory] = useState<string | null>(null);
  useEffect(() => {
    void api()
      .assistant.memory()
      .then(setMemory)
      .catch((e: unknown) => toast(errorText(e), 'error'));
  }, []);
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('馍馍的记忆')}
      labelledBy="memory-title"
      footer={
        <button className="btn" onClick={onClose}>
          {t('关闭')}
        </button>
      }
    >
      <p className="muted">{t('馍馍自己记下的事，帮它了解你，它会随时整理。想让它忘掉什么，直接在对话里告诉它。')}</p>
      {memory === null ? <span className="spinner" /> : <pre className="assistant-memory">{memory.trim() || t('还没有记下任何东西')}</pre>}
    </Dialog>
  );
}

/** Earlier chats, by day (newest first): click one to continue it, × to delete it. */
function ChatHistory({ conversations, disabled, onOpen, onDelete }: { conversations: AssistantConversation[]; disabled: boolean; onOpen: (id: string) => void; onDelete: (id: string) => void }) {
  if (!conversations.length) return <div className="assistant__list"><p className="muted assistant__hello">{t('还没有以前的对话。聊完一段时间不说话，或者点"新对话"，这段就会收到这里。')}</p></div>;
  const day = (at: number) => {
    const d = new Date(at);
    const today = new Date();
    const days = Math.round((new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86_400_000);
    return days <= 0 ? t('今天') : days === 1 ? t('昨天') : days < 7 ? t('这周') : t('更早');
  };
  const groups: [string, AssistantConversation[]][] = [];
  for (const c of conversations) {
    const label = day(c.updatedAt);
    const g = groups.find(([l]) => l === label);
    if (g) g[1].push(c);
    else groups.push([label, [c]]);
  }
  return (
    <div className="assistant__list assistant__history">
      {groups.map(([label, list]) => (
        <section key={label}>
          <h4 className="assistant__history-day">{label}</h4>
          {list.map((c) => (
            <div key={c.id} className="assistant__chat">
              <button className="assistant__chat-open" disabled={disabled} onClick={() => onOpen(c.id)}>
                <span className="assistant__chat-title">{c.title}</span>
                <span className="muted assistant__chat-meta">{new Date(c.updatedAt).toLocaleString([], { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </button>
              <button className="btn btn--icon btn--quiet" title={t('删除这段对话')} aria-label={t('删除这段对话')} onClick={() => onDelete(c.id)}>
                <Icon name="x" size={14} />
              </button>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
